import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export function HelpTab({ authToken }) {
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEntries, setExpandedEntries] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${authToken}` }
  }), [authToken]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [catRes, entryRes] = await Promise.all([
          axios.get(`${API_BASE}/api/faq/categories`, authHeaders()),
          axios.get(`${API_BASE}/api/faq/entries`, authHeaders())
        ]);
        setCategories(catRes.data || []);
        setEntries(entryRes.data || []);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    load();
  }, [authHeaders]);

  const toggleEntry = (entryId) => {
    setExpandedEntries(prev => ({
      ...prev,
      [entryId]: !prev[entryId]
    }));
  };

  // Filter-Funktion
  const filteredEntries = searchTerm.trim()
    ? entries.filter(e => 
        e.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.answer.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : entries;

  const filteredCategories = searchTerm.trim()
    ? categories.filter(cat => 
        filteredEntries.some(e => e.category_id === cat.id)
      )
    : categories;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-violet-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="rounded-xl bg-slate-50 p-8 text-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-4 text-sm font-medium text-slate-600">Keine Hilfe-Einträge vorhanden</h3>
        <p className="mt-1 text-xs text-slate-400">Der Administrator kann FAQ-Einträge in den Systemeinstellungen hinzufügen.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="help-tab">
      {/* Header */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-100">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Hilfe & FAQ</h2>
            <p className="text-sm text-slate-500">Häufig gestellte Fragen und Anleitungen</p>
          </div>
        </div>

        {/* Suchfeld */}
        <div className="mt-4 relative">
          <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Suche in FAQ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-4 text-sm focus:border-violet-500 focus:outline-none"
          />
        </div>
      </div>

      {/* FAQ-Kategorien und Einträge */}
      {filteredCategories.length === 0 ? (
        <div className="rounded-xl bg-slate-50 p-6 text-center">
          <p className="text-sm text-slate-500">Keine Ergebnisse für "{searchTerm}"</p>
        </div>
      ) : (
        filteredCategories.map((category) => {
          const catEntries = filteredEntries.filter(e => e.category_id === category.id);
          if (catEntries.length === 0) return null;

          return (
            <div key={category.id} className="rounded-xl bg-white shadow-sm overflow-hidden">
              {/* Kategorie-Header */}
              <div className="bg-gradient-to-r from-violet-50 to-white px-4 py-3 border-b border-slate-100">
                <h3 className="text-lg font-bold text-violet-900">{category.name}</h3>
                <p className="text-sm text-slate-500">{catEntries.length} {catEntries.length === 1 ? 'Eintrag' : 'Einträge'}</p>
              </div>

              {/* Einträge als Akkordeon */}
              <div className="divide-y divide-slate-100">
                {catEntries.map((entry) => (
                  <div key={entry.id}>
                    <button
                      onClick={() => toggleEntry(entry.id)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="text-base font-semibold text-slate-800 pr-4">{entry.question}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-5 w-5 text-slate-400 transition-transform ${expandedEntries[entry.id] ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedEntries[entry.id] && (
                      <div className="px-4 pb-4 bg-slate-50">
                        <div 
                          className="prose prose-sm max-w-none text-slate-600"
                          dangerouslySetInnerHTML={{ __html: entry.answer }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
